import type React from 'react';
import type { ReactNode } from 'react';
import { DropdownNavigationItem } from '../dropdown-navigation-item';
import { Icon, Icons } from '../icon';
import { cn } from '../lib/utils';
import { Button } from '../primitives/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../primitives/dropdown-menu';

export type DropdownNavigationMenuItem =
  | {
      icon: (typeof Icons)[keyof typeof Icons];
      label: string;
      onSelect?: () => void;
      onClick?: (e: React.MouseEvent) => void;
      iconClassName?: string;
      rightAddon?: ReactNode;
    }
  | { separator: true };

export interface DropdownNavigationMenuProps
  extends Omit<React.ComponentPropsWithoutRef<'button'>, 'children'> {
  /**
   * Array of menu items to display
   */
  items: DropdownNavigationMenuItem[];
  /**
   * Whether the menu is open (controlled)
   */
  isOpen?: boolean;
  /**
   * Callback when the open state changes
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Optional className for the trigger button
   */
  triggerClassName?: string;
  /**
   * Alignment of the dropdown menu
   * @default "start"
   */
  align?: 'start' | 'center' | 'end';
  /**
   * Which side of the trigger the menu opens from.
   * @default "bottom" (Radix default)
   */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Distance in px between trigger and menu.
   */
  sideOffset?: number;
  /**
   * Extra classes for the menu content (e.g. min-w / shadow overrides).
   */
  contentClassName?: string;
  /**
   * Icon to display in the trigger button
   * @default Icons.DotsThree
   */
  triggerIcon?: (typeof Icons)[keyof typeof Icons];
  /**
   * Optional className for the trigger icon
   * @default "text-s"
   */
  triggerIconClassName?: string;
}

const DEFAULT_TRIGGER_ICON = Icons.DotsThree;

export const DropdownNavigationMenu = ({
  items,
  isOpen,
  onOpenChange,
  triggerClassName,
  align = 'start',
  side,
  sideOffset,
  contentClassName,
  triggerIcon = DEFAULT_TRIGGER_ICON,
  triggerIconClassName,
  onClick,
  ...rest
}: DropdownNavigationMenuProps) => {
  return (
    <DropdownMenu open={isOpen} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            'absolute size-5 p-0 transition-opacity rounded-xs',
            'hover:bg-[rgba(0,0,0,0.05)]',
            triggerClassName,
          )}
          onClick={(e) => {
            e.stopPropagation();
            onClick?.(e);
          }}
          {...rest}
        >
          <Icon name={triggerIcon} className={cn('text-s', triggerIconClassName)} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        side={side}
        sideOffset={sideOffset}
        className={cn('rounded-2xl', contentClassName)}
      >
        {items.map((item, index) =>
          'separator' in item ? (
            // biome-ignore lint/suspicious/noArrayIndexKey: separator has no stable key
            <DropdownMenuSeparator key={index} className="bg-primary-alpha-5" />
          ) : (
            <DropdownNavigationItem
              key={item.label}
              icon={item.icon}
              label={item.label}
              onClick={item.onClick}
              onSelect={item.onSelect}
              iconClassName={item.iconClassName}
              rightAddon={item.rightAddon}
            />
          ),
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
